import { checkFeatureAvailability } from './version.js';
import { createVerifyToken } from './verifyToken.js';
import { AUTH_STATUS, LOGOUT_REASON, createAuthStore } from './authStore.js';
import { wrapLoggerWithAlert, defaultPostMessage } from './transport.js';

/**
 * @typedef {import('./authStore.js').AuthState} AuthState
 *
 * @typedef {Object} BridgeClaims
 * @property {string} [oid]
 * @property {string} [sub]
 * @property {string} [tid]
 * @property {string[]} [emails]
 *
 * @typedef {Object} BridgeConfig
 * @property {string|(() => string)} backendDomain
 * @property {string} [audience]
 * @property {string} [minAppVersion='3.0.0']
 * @property {(info: { claims: BridgeClaims & Record<string, unknown>, idToken: string, audience?: string }) => any} [onTokenVerified]
 *           Return value resolves the bridge's `login()` promise and is exposed
 *           via `getAuthState().account`. May be async; the bridge awaits it.
 * @property {(info: { reason: string }) => void|Promise<void>} [onLoggedOut]
 *           Called on every logout transition. `reason` distinguishes startup
 *           from explicit logout — see `LOGOUT_REASON`. May be async; the
 *           bridge awaits it before resolving the `ready` promise.
 * @property {(params: any) => void} [onAppParams]
 * @property {(info: { appVersion3: boolean, params: any }) => void} [onFeatureCheck]
 * @property {(err: unknown, ctx: { action: string, params: any }) => void} [onError]
 * @property {typeof fetch} [fetch]
 * @property {{ error?: Function, warn?: Function, info?: Function, debug?: Function }} [logger]
 * @property {boolean} [alertLogs] - When true, logger output is mirrored to `window.alert`.
 * @property {(data: string) => void} [postMessage] - Defaults to `window.ReactNativeWebView.postMessage`.
 */

/**
 * Create a bridge instance.
 *
 * @template TAccount
 * @param {BridgeConfig} config
 */
export function createBridge(config) {
    if (!config || !config.backendDomain) {
        throw new Error('createBridge: `backendDomain` is required');
    }

    const baseLogger = config.logger || console;
    const logger = config.alertLogs ? wrapLoggerWithAlert(baseLogger) : baseLogger;
    const minAppVersion = config.minAppVersion || '3.0.0';
    const postMessage = config.postMessage || defaultPostMessage();

    const authStore = createAuthStore(logger);

    /** Ready promise: resolves on the first transition out of `unknown`. */
    let resolveReady;
    const ready = new Promise((resolve) => { resolveReady = resolve; });
    const unsubscribeReady = authStore.subscribe((s) => {
        if (s.status === AUTH_STATUS.UNKNOWN) return;
        unsubscribeReady();
        resolveReady(s);
    });

    /** Resolves on the next successful `login` action. */
    let pendingLogin = null;

    // ----- emitters: single point that updates store + invokes host hooks ----

    async function emitAuthenticated(account) {
        authStore.set({ status: AUTH_STATUS.AUTHENTICATED, account });
        return account;
    }

    /**
     * Mark the user logged-out and invoke `onLoggedOut`. Awaits the host
     * callback (e.g. `instance.clearCache()`) so the bridge does not signal
     * readiness until cleanup has finished.
     */
    async function emitLoggedOut(reason) {
        authStore.set({ status: AUTH_STATUS.UNAUTHENTICATED, account: null, reason });
        if (typeof config.onLoggedOut !== 'function') return;
        try {
            await config.onLoggedOut({ reason });
        } catch (err) {
            logger.error && logger.error('adxdb-bridge: onLoggedOut threw', err);
        }
    }

    function emitError(err, ctx) {
        if (typeof config.onError !== 'function') return;
        try { config.onError(err, ctx); }
        catch (e) { logger.error && logger.error('adxdb-bridge: onError threw', e); }
    }

    // ----- verifyToken wired through the store -------------------------------
    //
    // We deliberately do NOT pass `config.onLoggedOut` to `createVerifyToken`:
    // the bridge owns the logout-reason taxonomy. The standalone
    // `createVerifyToken` export still honours `onLoggedOut` for direct callers.

    const verifyTokenInner = createVerifyToken({
        backendDomain: config.backendDomain,
        fetch: config.fetch,
        onTokenVerified: config.onTokenVerified,
        logger,
    });

    async function verifyToken(token, audience) {
        try {
            const account = await verifyTokenInner(token, audience);
            await emitAuthenticated(account);
            return account;
        } catch (err) {
            await emitLoggedOut(LOGOUT_REASON.VERIFY_FAILED);
            throw err;
        }
    }

    // ----- built-in actions --------------------------------------------------

    /** @type {Record<string, (params: any, ctx: any) => any>} */
    const actions = Object.create(null);

    actions.getAppParams = async function (params) {
        const feature = checkFeatureAvailability(params || {}, minAppVersion);
        const appVersion3 = feature.appVersion3;
        state.appVersion3 = appVersion3;

        if (typeof config.onFeatureCheck === 'function') {
            try { config.onFeatureCheck({ appVersion3, params }); }
            catch (err) { logger.error && logger.error(err); }
        }
        if (typeof config.onAppParams === 'function') {
            try { config.onAppParams(params); }
            catch (err) { logger.error && logger.error(err); }
        }

        if (appVersion3) {
            bridge.callNativeAction('checkState', {});
        } else {
            await emitLoggedOut(LOGOUT_REASON.APP_VERSION_TOO_LOW);
        }
    };

    actions.checkState = async function (params) {
        logger.debug && logger.debug('adxdb-bridge: checkState received', params);
        const result = params && params.result;

        if (!result || !result.success) {
            logger.debug && logger.debug('adxdb-bridge: checkState no session', result);
            await emitLoggedOut(LOGOUT_REASON.NO_SESSION);
            return;
        }

        logger.debug && logger.debug(
            'adxdb-bridge: checkState success, verifying idToken prefix',
            result.idToken ? result.idToken.substring(0, 20) + '...' : 'MISSING',
        );

        try {
            await verifyToken(result.idToken, config.audience);
        } catch (err) {
            // Surface startup verification failures so hosts can show
            // "session expired" instead of getting stuck on an unknown state.
            // (`emitLoggedOut` has already fired inside `verifyToken`.)
            emitError(err, { action: 'checkState', params });
        }
    };

    actions.login = async function (params) {
        logger.debug && logger.debug('adxdb-bridge: login action received', params);
        const result = params && params.result;

        if (!result || !result.success) {
            logger.debug && logger.debug('adxdb-bridge: login failed', result);
            if (pendingLogin) {
                pendingLogin.reject(new Error('login failed'));
                pendingLogin = null;
            }
            await emitLoggedOut(LOGOUT_REASON.LOGIN_FAILED);
            return;
        }

        logger.debug && logger.debug(
            'adxdb-bridge: login success, verifying idToken prefix',
            result.idToken ? result.idToken.substring(0, 20) + '...' : 'MISSING',
        );

        // `onTokenVerified` is the single source of truth for post-login
        // hydration — we deliberately do NOT also call a separate
        // `onLoginSuccess` to avoid running the same logic twice. Hosts that
        // need a "login completed" hook chain on the promise from `login()`.
        try {
            const account = await verifyToken(result.idToken, config.audience);
            if (pendingLogin) {
                pendingLogin.resolve(account);
                pendingLogin = null;
            }
        } catch (err) {
            if (pendingLogin) {
                pendingLogin.reject(err);
                pendingLogin = null;
            }
            emitError(err, { action: 'login', params });
        }
    };

    actions.logout = async function (_params) {
        // Always redirect on a logout callback, regardless of `success` flag.
        // If the native side reports back at all, the user is logged out.
        await emitLoggedOut(LOGOUT_REASON.LOGOUT_CONFIRMED);
    };

    // ----- shared state passed into custom action handlers -------------------

    const state = { appVersion3: true };

    // ----- public API --------------------------------------------------------

    const bridge = {
        /**
         * Post an action to the native host.
         * @param {string} action
         * @param {any} params
         */
        callNativeAction(action, params) {
            const data = action + '/' + JSON.stringify(params);
            if (typeof postMessage !== 'function') {
                logger.warn && logger.warn(
                    'adxdb-bridge: no native transport available for', action,
                );
                return;
            }
            try {
                postMessage(data);
            } catch (err) {
                logger.error && logger.error(
                    'adxdb-bridge: postMessage failed for data', data, err,
                );
            }
        },

        /**
         * Entry point invoked by the native app when a previously dispatched
         * action resolves. The native contract is preserved: this function
         * always synchronously returns `'{"success":true}'`. Async handlers
         * still run; their failures are logged and routed to `onError`.
         *
         * @param {string} action
         * @param {string} paramsJson
         * @returns {string}
         */
        nativeBridgeReturn(action, paramsJson) {
            let parsed;
            try {
                parsed = typeof paramsJson === 'string' ? JSON.parse(paramsJson) : paramsJson;
            } catch (err) {
                logger.error && logger.error(
                    'adxdb-bridge: failed to parse nativeBridgeReturn params for', action, err,
                );
                parsed = {};
            }

            const handler = actions[action];
            if (!handler) {
                logger.warn && logger.warn('adxdb-bridge: no handler registered for action', action);
                return JSON.stringify({ success: true });
            }

            let outcome;
            try {
                outcome = handler(parsed, { bridge, config, state });
            } catch (err) {
                emitError(err, { action, params: parsed });
                logger.error && logger.error('adxdb-bridge: action handler threw', action, err);
                return JSON.stringify({ success: true });
            }

            if (outcome && typeof outcome.then === 'function') {
                outcome.catch((err) => {
                    emitError(err, { action, params: parsed });
                    logger.error && logger.error('adxdb-bridge: async action handler rejected', action, err);
                });
            }

            return JSON.stringify({ success: true });
        },

        /**
         * Register (or override) an action handler.
         * @param {string} name
         * @param {(params: any, ctx: { bridge: any, config: BridgeConfig, state: any }) => any} handler
         */
        registerAction(name, handler) {
            if (typeof handler !== 'function') {
                throw new Error('registerAction: handler must be a function');
            }
            actions[name] = handler;
        },

        /**
         * Trigger a login on the native side and resolve once the subsequent
         * `login` action reports success.
         * @returns {Promise<TAccount>}
         */
        login() {
            if (pendingLogin) return pendingLogin.promise;
            let resolve, reject;
            const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
            pendingLogin = { promise, resolve, reject };
            bridge.callNativeAction('login', {});
            return promise;
        },

        /**
         * Trigger a logout on the native side and immediately notify the host
         * (`onLoggedOut`) so the WebView can redirect to its login page without
         * waiting for the native callback round-trip.
         *
         * Two `onLoggedOut` invocations may occur per logout:
         *   1. eager:     `{ reason: 'logout-eager' }`
         *   2. confirmed: `{ reason: 'logout-confirmed' }` (from native)
         *
         * The auth-state observable only notifies subscribers once because the
         * second transition is a no-op (already `unauthenticated`).
         */
        logout() {
            bridge.callNativeAction('logout', {});
            // fire-and-forget: bridge.logout() is sync by contract.
            emitLoggedOut(LOGOUT_REASON.LOGOUT_EAGER);
        },

        /** Direct access if the host needs to verify outside the action flow. */
        verifyToken,

        isAppVersion3() { return state.appVersion3; },

        /**
         * Kick off the native handshake (`getAppParams`). Safe to call when no
         * native transport is available; simply no-ops and returns `false`.
         */
        start() {
            if (typeof postMessage !== 'function') return false;
            bridge.callNativeAction('getAppParams', {});
            return true;
        },

        // ----- auth-state observable -----

        /**
         * Promise that resolves on the first transition out of `unknown`,
         * i.e. once the native handshake (or the first `login()` /
         * `verifyToken()` call) has settled. Resolves with the current
         * `AuthState`.
         *
         * @type {Promise<AuthState>}
         */
        ready,

        /** @returns {AuthState} */
        getAuthState() { return authStore.get(); },

        /**
         * Subscribe to auth-state changes. Returns an unsubscribe function.
         * The listener is NOT called eagerly with the current value — call
         * `getAuthState()` first if you need that.
         *
         * @param {(s: AuthState) => void} listener
         * @returns {() => void}
         */
        subscribeAuth(listener) { return authStore.subscribe(listener); },
    };

    return bridge;
}

export { AUTH_STATUS, LOGOUT_REASON };
